'use client'

import { type FC, useMemo } from 'react'
import { JsonLd } from 'react-schemaorg'
import {
  type BreadcrumbList,
  type ListItem,
  type WithContext,
} from 'schema-dts'

export type SeoBreadcrumbListItem = Pick<ListItem, 'item' | 'name'>

/**
 * Only string `name` and URL `item` (except last item https://developers.google.com/search/docs/appearance/structured-data/breadcrumb#list-item) are required,
 * rest is provided by `SeoBreadcrumbListJsonLd` component
 */
export type SeoBreadcrumbListItems = SeoBreadcrumbListItem[] | null

type SeoBreadcrumbListJsonLdProps = {
  breadcrumbs: SeoBreadcrumbListItems
}

export const SeoBreadcrumbListJsonLd: FC<SeoBreadcrumbListJsonLdProps> = ({
  breadcrumbs,
}) => {
  const itemListElement = useMemo(
    () =>
      (breadcrumbs ?? []).map<ListItem>((listItem, index) => ({
        ...listItem,
        '@type': 'ListItem',
        position: index + 1,
      })),
    [breadcrumbs],
  )

  const item = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  } satisfies WithContext<BreadcrumbList>

  return <JsonLd<BreadcrumbList> item={item} />
}
